import { looksLikeClinicalHtml } from './clinicalHtml';

const NUMBERED_LINE_RE = /^(\d+)\.\s+(.+)$/;
const LEGEND_LINE_RE = /^Legenda:/i;
const EXAM_SECTION_HEADER_RE = /^Exames solicitados:/i;
const EXAM_META_LINE_RE = /^(Indicação clínica|Diagnóstico|Instruções|Material|Região|Lateralidade|Preparo|Justificativa):/i;

function toPlainMultiline(raw: string): string {
  if (!raw?.trim()) return '';
  if (!looksLikeClinicalHtml(raw)) return String(raw).replace(/\r\n/g, '\n').trim();

  return String(raw)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function extractExamListItems(text: string): string[] {
  const items: string[] = [];
  let inExamSection = false;

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (EXAM_SECTION_HEADER_RE.test(trimmed)) {
      inExamSection = true;
      continue;
    }
    if (LEGEND_LINE_RE.test(trimmed)) break;
    if (!inExamSection) continue;
    if (EXAM_META_LINE_RE.test(trimmed) || /^\s/.test(line)) continue;

    const numbered = trimmed.match(NUMBERED_LINE_RE);
    if (numbered) {
      items.push(numbered[2].trim());
      continue;
    }
    items.push(trimmed);
  }

  if (items.length > 0) return items;

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    const match = trimmed.match(NUMBERED_LINE_RE);
    if (match) items.push(match[2].trim());
  }
  return items;
}

function extractHeaderLines(text: string): string[] {
  const header: string[] = [];
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (header.length > 0) break;
      continue;
    }
    if (LEGEND_LINE_RE.test(trimmed)) break;
    if (EXAM_SECTION_HEADER_RE.test(trimmed)) {
      header.push(trimmed);
      break;
    }
    if (NUMBERED_LINE_RE.test(trimmed)) break;
    header.push(trimmed);
  }
  return header;
}

function extractLegendLine(text: string): string {
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (LEGEND_LINE_RE.test(trimmed)) return trimmed;
  }
  return '';
}

/** Combina dois corpos de pedido de exame, unificando a lista de exames sem numeração. */
export function mergeExamOrderEditorBodies(base: string, addition: string): string {
  const basePlain = toPlainMultiline(base);
  const addPlain = toPlainMultiline(addition);

  if (!basePlain.trim()) return addPlain;
  if (!addPlain.trim()) return basePlain;

  const baseItems = extractExamListItems(basePlain);
  const addItems = extractExamListItems(addPlain);

  if (baseItems.length > 0 || addItems.length > 0) {
    const allItems = [...baseItems, ...addItems];
    const header =
      extractHeaderLines(basePlain).length > 0
        ? extractHeaderLines(basePlain)
        : extractHeaderLines(addPlain);
    const legend = extractLegendLine(basePlain) || extractLegendLine(addPlain);
    const hasExamHeader = header.some((line) => /^Exames solicitados:/i.test(line));

    const lines = [...header];
    if (allItems.length > 0) {
      if (!hasExamHeader) {
        if (lines.length > 0 && lines[lines.length - 1] !== '') lines.push('');
        lines.push('Exames solicitados:');
      } else if (lines.length > 0 && lines[lines.length - 1] !== '') {
        lines.push('');
      }
      lines.push(...allItems);
    }
    if (legend) lines.push('', legend);
    return lines.join('\n').trim();
  }

  return `${basePlain}\n\n${addPlain}`.trim();
}
